import { call, CallEffect, select, SelectEffect, takeEvery } from 'redux-saga/effects';

import { SHOW_LEFT_HAND_PANEL, ShowLeftHandPanelAction } from 'js/actionCreators/uiActions';
import { SHOW_IMAGE_GENERATION_VIEW, ShowImageGenerationViewAction } from 'js/actionCreators/ImageDrawerActions';
import {
  AiPanelTab,
  ExistingScribeModel,
  ImageGenerationInputs,
  ImageGenerationOutputs,
  ScriptGenerationInputs,
  VoiceoverGenerationOutputs,
  LeftHandPanel
} from 'js/types';
import {
  ChangeStepTrigger,
  GENERATE_IMAGES,
  SET_IMAGE_GENERATION_INPUTS,
  SetImageGenerationInputsAction,
  REGENERATE_IMAGES,
  TRY_NEW_IMAGE_PROMPT,
  GENERATE_SCRIPT,
  SAVE_GENERATED_SCRIPT,
  TRY_NEW_SCRIPT_PROMPT,
  TryNewScriptPromptAction,
  EDIT_IMAGE_PROMPT,
  SUBMIT_IMAGE_FEEDBACK,
  SubmitImageFeedbackAction,
  SubmitScriptFeedbackAction,
  SUBMIT_SCRIPT_FEEDBACK
} from 'js/actionCreators/aiFeaturesActions';
import {
  getImageGenerationInputs,
  getImageGenerationResults,
  getScriptGenerationInputs,
  getVoiceoverGenerationResults
} from 'js/sagas/aiFeaturesSagas';
import getProjectFromPathname from 'js/sagas/sagaHelpers/getProjectFromPathname';
import {
  generateVoiceover,
  previewVoiceoverVoice,
  setSelectedLanguage,
  setSelectedRegion,
  setSelectedVoice,
  setVoiceoverStage,
  VoiceoverGenerationStage,
  VoiceoversState
} from 'js/reducers/voiceoversReducer';
import { getCountriesByLanguage } from 'js/editor/AudioDrawers/voices';

import { track } from '../mixpanelProvider';

function getPromptProps(prompt: string) {
  let promptProps: { Prompt: string; 'Prompt (cont.)'?: string } = {
    Prompt: prompt
  };
  if (prompt.length > MIXPANEL_MAX_VALUE_LENGTH) {
    promptProps = {
      Prompt: prompt.substring(0, MIXPANEL_MAX_VALUE_LENGTH),
      'Prompt (cont.)': prompt.substring(MIXPANEL_MAX_VALUE_LENGTH)
    };
  }

  return promptProps;
}

function* trackShowAiPanel(
  action: ShowLeftHandPanelAction
): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  if (action.name !== LeftHandPanel.AI_GENERATION) return;
  const project = yield call(getProjectFromPathname);
  if (!project) return;

  const { trigger, tab } = action;

  if (trigger === ChangeStepTrigger.LEFT_HAND_PANEL) {
    yield call(track, 'Open AI LHP', { 'Scribe ID': project.id });
  }
  if (tab) {
    if (tab === AiPanelTab.IMAGE_GENERATION) {
      yield call(track, 'Open AI Image Gen', { 'Scribe ID': project.id, 'Event Trigger': trigger });
    } else if (tab === AiPanelTab.VOICE_GENERATION) {
      yield call(track, 'Open AI VO Gen', { 'Scribe ID': project.id, 'Event Trigger': trigger });
    } else if (tab === AiPanelTab.SCRIPT_GENERATION) {
      yield call(track, 'Open AI Script Gen', { 'Scribe ID': project.id, 'Event Trigger': trigger });
    }
  }
}

export function* trackOpenImageGeneration({
  trigger
}: ShowImageGenerationViewAction): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  const project = yield call(getProjectFromPathname);
  if (!project) return;

  yield call(track, 'Open AI Image Gen', { 'Scribe ID': project.id, 'Event Trigger': trigger });
}

function* trackVoiceoverStageChange(
  action: ReturnType<typeof setVoiceoverStage>
): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  const project = yield call(getProjectFromPathname);
  if (!project) return;

  if (action.payload.stage === VoiceoverGenerationStage.Voice) {
    yield call(track, 'Entered Choose Voice Panel', { 'Scribe ID': project.id });
  }
}

function* trackSelectedImageGenerationStyle({
  style
}: SetImageGenerationInputsAction): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  const project = yield call(getProjectFromPathname);
  if (!project) return;

  if (style) {
    yield call(track, 'Select AI Image Gen Style', { 'Scribe ID': project.id, 'Image Style Selected': style });
  }
}

const MIXPANEL_MAX_VALUE_LENGTH = 255;
function* trackGenerateImage(): Generator<
  CallEffect | SelectEffect,
  void,
  ExistingScribeModel | ImageGenerationInputs | undefined
> {
  const project = (yield call(getProjectFromPathname)) as ExistingScribeModel | undefined;
  if (!project) return;

  const { style, prompt } = (yield select(getImageGenerationInputs)) as ImageGenerationInputs;

  const promptProps = getPromptProps(prompt);
  yield call(track, 'Generate AI Image', { 'Scribe ID': project.id, 'Image Style Selected': style, ...promptProps });
}

type ImageGenerationBasicData = {
  'Image Style Selected': string;
  Prompt: string;
  'Scribe ID': number;
  'Generation ID': string;
  'Prompt (cont.)'?: string;
};

export function* getImageGenerationBasicData(): Generator<
  SelectEffect | CallEffect,
  ImageGenerationBasicData | undefined,
  ExistingScribeModel | undefined | ImageGenerationInputs | ImageGenerationOutputs
> {
  const { style, prompt } = (yield select(getImageGenerationInputs)) as ImageGenerationInputs;
  const { id } = (yield select(getImageGenerationResults)) as ImageGenerationOutputs;

  const project = (yield call(getProjectFromPathname)) as ExistingScribeModel | undefined;
  if (!project || !style || !id) return;
  return {
    'Image Style Selected': style,
    'Scribe ID': project.id,
    'Generation ID': id,
    ...getPromptProps(prompt)
  };
}

function* trackRegenerateImage(): Generator<CallEffect, void, ImageGenerationBasicData> {
  const basicData = (yield call(getImageGenerationBasicData)) as ImageGenerationBasicData | undefined;
  if (!basicData) return;
  yield call(track, 'Regenerate Image', {
    ...basicData
  });
}

function* trackEditImagePrompt(): Generator<
  CallEffect | SelectEffect,
  void,
  ExistingScribeModel | ImageGenerationInputs | undefined
> {
  const project = (yield call(getProjectFromPathname)) as ExistingScribeModel | undefined;
  if (!project) return;
  const { prompt, style } = (yield select(getImageGenerationInputs)) as ImageGenerationInputs;
  yield call(track, 'Edit AI Image Gen Prompt', {
    'Scribe ID': project.id,
    'Image Style Selected': style,
    ...getPromptProps(prompt)
  });
}

function* trackTryNewImagePrompt(): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  const project = (yield call(getProjectFromPathname)) as ExistingScribeModel | undefined;
  if (!project) return;
  yield call(track, 'Edit Image Gen Prompt', {
    'Scribe ID': project.id
  });
}

function* trackSelectVoiceoverLanguage(
  action: ReturnType<typeof setSelectedLanguage>
): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  const project = yield call(getProjectFromPathname);
  const { selectedLanguage } = action.payload;
  if (!project || !selectedLanguage) return;

  const availableRegions = getCountriesByLanguage(action.payload.selectedLanguage);

  if (availableRegions.length === 1) {
    yield call(track, 'Select AI VO Gen Language & Accent', {
      'Language & Accent': availableRegions[0].countryCode,
      'Scribe ID': project.id
    });
  }
}

function* trackSelectVoiceoverRegion(
  action: ReturnType<typeof setSelectedRegion>
): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  const project = yield call(getProjectFromPathname);
  const { selectedRegion } = action.payload;
  if (!project || !selectedRegion) return;
  yield call(track, 'Select AI VO Gen Language & Accent', {
    'Language & Accent': selectedRegion,
    'Scribe ID': project.id
  });
}

function* trackSelectVoiceoverVoice(
  action: ReturnType<typeof setSelectedVoice>
): Generator<CallEffect | SelectEffect, void, ExistingScribeModel | undefined | VoiceoversState> {
  const { selectedVoice } = action.payload;
  const project = (yield call(getProjectFromPathname)) as ExistingScribeModel | undefined;
  const { selectedRegion } = (yield select(state => state.voiceovers)) as VoiceoversState;
  if (!project || !selectedVoice) return;

  yield call(track, 'Select AI VO Profile', {
    'Language & Accent': selectedRegion,
    Gender: selectedVoice.gender,
    Voice: selectedVoice.name,
    'Scribe ID': project.id
  });
}

function* trackPreviewVoiceoverVoice({
  payload: { language, voice, gender }
}: ReturnType<typeof previewVoiceoverVoice>): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  const project = yield call(getProjectFromPathname);
  if (!project) return;
  yield call(track, 'Preview AI Example VO Profile', {
    'Language & Accent': language,
    Gender: gender,
    Voice: voice,
    'Scribe ID': project.id
  });
}

function* trackGenerateVoiceover(): Generator<
  CallEffect | SelectEffect,
  void,
  ExistingScribeModel | VoiceoversState | undefined
> {
  const project = (yield call(getProjectFromPathname)) as ExistingScribeModel | undefined;

  if (!project) return;

  const { selectedRegion, selectedVoice, scriptText } = (yield select(state => state.voiceovers)) as VoiceoversState;

  if (selectedVoice) {
    yield call(track, 'Click Generate AI Voiceover', {
      'Language & Accent': selectedRegion,
      Gender: selectedVoice.gender,
      Voice: selectedVoice.name,
      'Number of Characters': scriptText.length,
      'Scribe ID': project.id
    });
  }
}

export type VoiceoverGenerationBasicData = {
  'Language & Accent': string;
  Voice: string;
  Gender: string;
  'Scribe ID': number;
  'Audio ID': string;
};

export function* getVoiceoverGenerationBasicData(): Generator<
  SelectEffect | CallEffect,
  VoiceoverGenerationBasicData | undefined,
  ExistingScribeModel | undefined | VoiceoverGenerationOutputs | VoiceoversState
> {
  const { selectedRegion, selectedVoice } = (yield select(state => state.voiceovers)) as VoiceoversState;
  const { id } = (yield select(getVoiceoverGenerationResults)) as VoiceoverGenerationOutputs;

  const project = (yield call(getProjectFromPathname)) as ExistingScribeModel | undefined;
  if (!project || !selectedRegion || !selectedVoice || !id) return;
  return {
    'Language & Accent': selectedRegion,
    Voice: selectedVoice.name,
    Gender: selectedVoice.gender,
    'Scribe ID': project.id,
    'Audio ID': id
  };
}

function getScriptTrackingProperties(inputs: ScriptGenerationInputs) {
  const lengthOfScript = inputs.lengthOfScriptMinutes * 60 + inputs.lengthOfScriptSeconds;
  return {
    'Duration of Script (seconds)': lengthOfScript,
    'Style/Tone Options': inputs.predefinedStyles.filter(option => option.isChecked).map(option => option.label),
    'Style/Tone Other': inputs.customStyle,
    'Prompt Character Length': Array.from(inputs.prompt).length
  };
}

function* trackGenerateScript(): Generator<
  CallEffect | SelectEffect,
  void,
  ExistingScribeModel | ScriptGenerationInputs | undefined
> {
  const project = (yield call(getProjectFromPathname)) as ExistingScribeModel | undefined;
  if (!project) return;

  const inputs = (yield select(getScriptGenerationInputs)) as ScriptGenerationInputs;

  yield call(track, 'Click Generate AI Script', {
    'Scribe ID': project.id,
    ...getScriptTrackingProperties(inputs)
  });
}

function* submitScriptFeedback(
  action: SubmitScriptFeedbackAction
): Generator<SelectEffect | CallEffect, void, ExistingScribeModel | undefined | ScriptGenerationInputs> {
  const project = yield call(getProjectFromPathname);
  if (!project) return;
  const { rating, tags, comment } = action;
  const inputs = yield select(getScriptGenerationInputs);

  if (!!inputs && 'lengthOfScriptMinutes' in inputs && 'id' in project) {
    yield call(track, 'How is the generated script?', {
      'Scribe ID': project.id,
      'Script Rating': rating,
      Options: tags,
      Comment: comment,
      ...getScriptTrackingProperties(inputs)
    });
  }
}

function* submitImageFeedback(
  action: SubmitImageFeedbackAction
): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  const project = yield call(getProjectFromPathname);
  if (!project) return;
  const basicData = (yield call(getImageGenerationBasicData)) as ImageGenerationBasicData | undefined;
  const { imageId, rating, comment, options } = action.feedbackData;
  yield call(track, 'How is the generated image?', {
    ...basicData,
    'Scribe ID': project.id,
    'Image ID': imageId,
    'Image Rating': rating,
    Comment: comment,
    Tags: options.join(', ')
  });
}

function* trackSaveGeneratedScript(): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  const project = yield call(getProjectFromPathname);
  if (!project) return;

  yield call(track, 'Click Save Script Button', {
    'Scribe ID': project.id
  });
}

function* trackTryNewScriptPrompt(
  action: TryNewScriptPromptAction
): Generator<CallEffect, void, ExistingScribeModel | undefined> {
  const project = yield call(getProjectFromPathname);
  if (!project) return;

  if (action.reset) {
    yield call(track, 'Click Generate A New Script Button', {
      'Scribe ID': project.id
    });
  } else {
    yield call(track, 'Click Edit The Prompt Button', {
      'Scribe ID': project.id
    });
  }
}

export default function* trackAiSagas() {
  yield takeEvery(SHOW_IMAGE_GENERATION_VIEW, trackOpenImageGeneration);
  yield takeEvery(SHOW_LEFT_HAND_PANEL, trackShowAiPanel);
  yield takeEvery(setVoiceoverStage.type, trackVoiceoverStageChange);
  yield takeEvery(SET_IMAGE_GENERATION_INPUTS, trackSelectedImageGenerationStyle);
  yield takeEvery(GENERATE_IMAGES, trackGenerateImage);
  yield takeEvery(REGENERATE_IMAGES, trackRegenerateImage);
  yield takeEvery(EDIT_IMAGE_PROMPT, trackEditImagePrompt);
  yield takeEvery(TRY_NEW_IMAGE_PROMPT, trackTryNewImagePrompt);
  yield takeEvery(setSelectedLanguage.type, trackSelectVoiceoverLanguage);
  yield takeEvery(setSelectedRegion.type, trackSelectVoiceoverRegion);
  yield takeEvery(setSelectedVoice.type, trackSelectVoiceoverVoice);
  yield takeEvery(previewVoiceoverVoice.type, trackPreviewVoiceoverVoice);
  yield takeEvery(generateVoiceover.type, trackGenerateVoiceover);
  yield takeEvery(GENERATE_SCRIPT, trackGenerateScript);
  yield takeEvery(SAVE_GENERATED_SCRIPT, trackSaveGeneratedScript);
  yield takeEvery(TRY_NEW_SCRIPT_PROMPT, trackTryNewScriptPrompt);
  yield takeEvery(SUBMIT_IMAGE_FEEDBACK, submitImageFeedback);
  yield takeEvery(SUBMIT_SCRIPT_FEEDBACK, submitScriptFeedback);
}
